import "./styles.css";
import { ReactNode } from "react";
import { CounterInputV2, DropdownV2, FormCardV2, FormGridV2, InputV2, OptionV2 } from "../FormControlsV2";
import { useTranslation } from "react-i18next";

export type ProductFormFieldV2 =
  | "trade_name"
  | "manufacturer"
  | "classification"
  | "sub_classification"
  | "packaging_type"
  | "bag_weight_kg"
  | "expected_consumption"
  | "supply_frequency";

export type ProductFormValueV2 = {
  id?: string | number;
  creep_feeding: boolean;
  trade_name: string;
  manufacturer: string;
  classification: OptionV2 | null;
  sub_classification: OptionV2 | null;
  packaging_type: ProductPackagingType;
  bag_weight_kg: number | null;
  expected_consumption: number;
  supply_frequency: number;
};

export type ProductFormErrorsV2 = Partial<Record<ProductFormFieldV2, string>>;

type ProductFormV2Props = {
  value: ProductFormValueV2;
  errors?: ProductFormErrorsV2;
  classifications: OptionV2[];
  subClassificationLabel?: string;
  sideSlot?: ReactNode;
  disabled?: boolean;
  onChange: (value: ProductFormValueV2) => void;
};

export function createEmptyProductFormValue(): ProductFormValueV2 {
  return {
    creep_feeding: false,
    trade_name: "",
    manufacturer: "",
    classification: null,
    sub_classification: null,
    packaging_type: "BULK",
    bag_weight_kg: null,
    expected_consumption: 0,
    supply_frequency: 0,
  };
}

export function ProductFormV2({
  value,
  errors = {},
  classifications,
  subClassificationLabel,
  sideSlot,
  disabled = false,
  onChange,
}: ProductFormV2Props) {
  const { t } = useTranslation();
  const subClassificationOptions = Array.isArray(value.classification?.info) ? value.classification?.info : [];
  const packagingOptions: OptionV2<ProductPackagingType>[] = [
    { label: t("formsV2.packagingBulk"), value: "BULK" },
    { label: t("formsV2.packagingBagged"), value: "BAGGED" },
  ];
  const expectedConsumptionSuffix = value.packaging_type === "BAGGED"
    ? t("productPackaging.bags")
    : "kg";

  function update(partial: Partial<ProductFormValueV2>) {
    onChange({ ...value, ...partial });
  }

  return (
    <FormCardV2>
      <div className={sideSlot ? "create-product-v2__card-layout" : ""}>
      <div className="create-product-v2__fields">
        <FormGridV2>
          <label className={[
            "ov2-product-creep-feeding",
            value.creep_feeding && "ov2-product-creep-feeding--checked",
            disabled && "ov2-product-creep-feeding--disabled",
          ].filter(Boolean).join(" ")}>
            <input
              type="checkbox"
              name="creep_feeding"
              checked={value.creep_feeding}
              disabled={disabled}
              onChange={(event) => update({ creep_feeding: event.target.checked })}
            />
            {t("formsV2.creepFeeding")}
          </label>
          <InputV2
            name="trade_name"
            label={t("formsV2.productTradeName")}
            value={value.trade_name}
            disabled={disabled}
            error={errors.trade_name}
            onChange={(trade_name) => update({ trade_name })}
          />
          <DropdownV2
            name="packaging_type"
            label={t("formsV2.packagingType")}
            value={packagingOptions.find((option) => option.value === value.packaging_type) || null}
            options={packagingOptions}
            disabled={disabled}
            error={errors.packaging_type}
            onChange={(option) => update({
              packaging_type: option?.value || "BULK",
              bag_weight_kg: option?.value === "BAGGED" ? value.bag_weight_kg : null,
            })}
          />
          {value.packaging_type === "BAGGED" && (
            <InputV2
              name="bag_weight_kg"
              type="number"
              label={t("formsV2.bagWeight")}
              value={value.bag_weight_kg ?? ""}
              suffix="kg"
              disabled={disabled}
              error={errors.bag_weight_kg}
              onChange={(bagWeight) => update({ bag_weight_kg: bagWeight === "" ? null : Number(bagWeight) })}
            />
          )}
          <InputV2
            name="manufacturer"
            label={t("formsV2.manufacturer")}
            value={value.manufacturer}
            disabled={disabled}
            error={errors.manufacturer}
            onChange={(manufacturer) => update({ manufacturer })}
          />
          <DropdownV2
            name="classification"
            label={t("formsV2.classification")}
            value={value.classification}
            options={classifications}
            searchable
            disabled={disabled}
            error={errors.classification}
            onChange={(classification) =>
              update({
                classification,
                sub_classification: null,
                expected_consumption: 0,
                supply_frequency: 0,
              })
            }
          />
          {value.classification && (
            <DropdownV2
              name="sub_classification"
              label={subClassificationLabel || t("formsV2.subClassification")}
              value={value.sub_classification}
              options={subClassificationOptions}
              searchable
              disabled={disabled}
              error={errors.sub_classification}
              onChange={(sub_classification) =>
                update({
                  sub_classification,
                  expected_consumption: 0,
                  supply_frequency: 0,
                })
              }
            />
          )}
          {value.sub_classification && (
            <CounterInputV2
              name="expected_consumption"
              allowDecimal
              label={t("formsV2.expectedConsumption")}
              value={value.expected_consumption}
              max={10000000}
              suffix="g/kg de PV"
              disabled={disabled}
              error={errors.expected_consumption}
              onChange={(expected_consumption) => update({ expected_consumption, supply_frequency: 0 })}
            />
          )}
          {value.expected_consumption > 0 && (
            <CounterInputV2
              name="supply_frequency"
              label={t("formsV2.supplyFrequency")}
              value={value.supply_frequency}
              max={7}
              suffix={t("formsV2.timesPerWeek")}
              disabled={disabled}
              error={errors.supply_frequency}
              onChange={(supply_frequency) => update({ supply_frequency })}
            />
          )}
        </FormGridV2>
      </div>
        {sideSlot && <div className="create-product-v2__side-slot">{sideSlot}</div>}
      </div>
    </FormCardV2>
  );
}
